import React, { useState, useEffect } from 'react';
import {
    MagnifyingGlassIcon,
    TrashIcon,
    PlusIcon,
    MinusIcon,
    UsersIcon,
    CheckIcon,
    XMarkIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import LayoutWrapper from '../components/LayoutWrapper';
import MemberSubscriptionCell from '../components/MemberSubscriptionCell';
import ResetSignupModal from '../components/ResetSignupModal';
import Helper from '../helpers';
import { fetchVenueUsers, softDeleteUserFromVenue, bulkChangeUserTokens } from '../Services/UserService.ts';
import { getSubscriptions } from '../Services/VenueService.ts';

const BulkUserManagement = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    // Standard plans an admin can pick when sending a member a fresh sign up link
    const [plans, setPlans] = useState([]);
    const [resetMember, setResetMember] = useState(null);
    const [venueKey, setVenueKey] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Selection
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');

    // Action state
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');

    // Token panel
    const [tokenInput, setTokenInput] = useState('');
    const [tokenMode, setTokenMode] = useState('add'); // 'add' | 'subtract'

    // Delete confirm modal
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // ── Load users ───────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            try {
                const vk = await Helper.getUserVenueId();
                setVenueKey(vk);
                if (vk) {
                    const data = await fetchVenueUsers(vk);
                    setUsers(data || []);

                    // Plans are only needed for the sign up link, so a failure here must not break the list
                    try {
                        const subs = await getSubscriptions(vk);
                        setPlans((subs?.items || []).filter(s => !s.isImported && !s.isCustomerSubscription));
                    } catch (planError) {
                        console.error('Could not load plans for the sign up link', planError);
                    }
                }
            } catch (e) {
                setError('Failed to load users. Please refresh and try again.');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    // ── Filtering ────────────────────────────────────────────────────────────
    const filtered = users.filter(u => {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            u.name?.toLowerCase().includes(term) ||
            u.email?.toLowerCase().includes(term);
        const matchesRole =
            filterRole === 'all' ||
            (filterRole === 'admin' && u.isAdmin) ||
            (filterRole === 'member' && !u.isAdmin) ||
            (filterRole === 'nosub' && !u.isAdmin && !u.hasSubscription);
        return matchesSearch && matchesRole;
    });

    // ── Selection helpers ────────────────────────────────────────────────────
    const allSelected = filtered.length > 0 && filtered.every(u => selectedIds.has(u.userId));
    const someSelected = filtered.some(u => selectedIds.has(u.userId));

    const toggleAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filtered.map(u => u.userId)));
        }
    };

    const toggleUser = (userId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    const selectedCount = selectedIds.size;

    // ── Actions ──────────────────────────────────────────────────────────────
    const clearMessages = () => {
        setActionError('');
        setActionSuccess('');
    };

    const handleBulkTokens = async () => {
        const qty = parseInt(tokenInput, 10);
        if (!tokenInput || isNaN(qty) || qty === 0) {
            setActionError('Please enter a valid non-zero token amount.');
            return;
        }
        clearMessages();
        setActionLoading(true);
        try {
            const delta = tokenMode === 'subtract' ? -Math.abs(qty) : Math.abs(qty);
            const ids = Array.from(selectedIds);
            await bulkChangeUserTokens(venueKey, ids, delta);
            // Update displayed token values optimistically
            setUsers(prev =>
                prev.map(u =>
                    selectedIds.has(u.userId)
                        ? { ...u, tokens: Math.max(0, (u.tokens || 0) + delta) }
                        : u
                )
            );
            setActionSuccess(
                `${tokenMode === 'subtract' ? 'Removed' : 'Added'} ${Math.abs(qty)} token(s) for ${ids.length} user(s).`
            );
            setTokenInput('');
        } catch (e) {
            setActionError(e.message || 'Failed to update tokens.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        clearMessages();
        setActionLoading(true);
        const ids = Array.from(selectedIds);
        let failed = 0;
        let sucessfullRemovedUsers = [];
        for (const userId of ids) {
            try {
                await softDeleteUserFromVenue(venueKey, userId);
                sucessfullRemovedUsers.push(userId);
            } catch {
                failed++;
            }
        }
        setUsers(prev => prev.filter(u => !sucessfullRemovedUsers.includes(u.userId)));
        setSelectedIds(new Set());
        setShowDeleteConfirm(false);
        if (failed > 0) {
            setActionError(`${failed} user(s) could not be removed. Others were removed successfully.`);
        } else {
            setActionSuccess(`${ids.length} user(s) successfully removed from venue.`);
        }
        setActionLoading(false);
    };

    // Manual page for linking a member to a subscription that already exists in GoCardless
    const goToGoCardlessLinking = () => navigate('/dashboard/import-users/gocardless');

    // ── Render ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <LayoutWrapper title="Bulk User Actions" subtitle="Manage users in bulk">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
                </div>
            </LayoutWrapper>
        );
    }

    if (error) {
        return (
            <LayoutWrapper title="Bulk User Actions" subtitle="Manage users in bulk">
                <div className="card-luxury p-8 text-center">
                    <ExclamationTriangleIcon className="h-12 w-12 text-performance-pulse mx-auto mb-4" />
                    <p className="text-performance-pulse font-body">{error}</p>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper title="Bulk User Actions" subtitle="Select users and apply bulk operations">

            {/* ── Action feedback ── */}
            {actionSuccess && (
                <div className="mb-4 p-4 rounded-xl border border-emerald-500/40 bg-emerald-900/20 flex items-start gap-3">
                    <CheckIcon className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <p className="text-emerald-300 text-sm font-body">{actionSuccess}</p>
                    <button onClick={() => setActionSuccess('')} className="ml-auto text-emerald-600 hover:text-emerald-400">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            )}
            {actionError && (
                <div className="mb-4 p-4 rounded-xl border border-performance-pulse/40 bg-performance-pulse/10 flex items-start gap-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-performance-pulse flex-shrink-0 mt-0.5" />
                    <p className="text-performance-pulse text-sm font-body">{actionError}</p>
                    <button onClick={() => setActionError('')} className="ml-auto text-performance-pulse/60 hover:text-performance-pulse">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* ── Search & filter bar ── */}
            <div className="card-luxury p-4 mb-4 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600/60" />
                    <input
                        type="text"
                        placeholder="Search by name or email…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-luxury-obsidian border border-emerald-700/30 rounded-lg text-accent-platinum text-sm placeholder-emerald-600/50 focus:outline-none focus:border-emerald-500/60"
                    />
                </div>
                <select
                    value={filterRole}
                    onChange={e => setFilterRole(e.target.value)}
                    className="px-3 py-2 bg-luxury-obsidian border border-emerald-700/30 rounded-lg text-accent-platinum text-sm focus:outline-none focus:border-emerald-500/60"
                >
                    <option value="all">All roles</option>
                    <option value="member">Members</option>
                    <option value="admin">Admins</option>
                    <option value="nosub">No subscription</option>
                </select>
                <div className="flex items-center text-sm text-emerald-600/70 font-body whitespace-nowrap">
                    <UsersIcon className="h-4 w-4 mr-1" />
                    {filtered.length} user{filtered.length !== 1 ? 's' : ''}
                </div>
            </div>

            {/* ── Bulk action toolbar (sticky, appears when selection > 0) ── */}
            {selectedCount > 0 && (
                <div className="sticky top-16 z-20 mb-4 card-luxury border border-emerald-600/40 p-4 shadow-luxury">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Selection info */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="w-6 h-6 rounded bg-emerald-600 flex items-center justify-center">
                                <CheckIcon className="h-4 w-4 text-white" />
                            </div>
                            <span className="text-emerald-300 font-display font-semibold text-sm">
                                {selectedCount} user{selectedCount !== 1 ? 's' : ''} selected
                            </span>
                            <button
                                onClick={clearSelection}
                                className="text-xs text-emerald-600/70 hover:text-emerald-400 underline ml-1"
                            >
                                Clear
                            </button>
                        </div>

                        <div className="flex-1 border-t lg:border-t-0 lg:border-l border-emerald-700/30 lg:pl-4 pt-3 lg:pt-0">
                            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">

                                {/* Token adjustment */}
                                <div className="flex items-center gap-2">
                                    <div className="flex rounded-lg overflow-hidden border border-emerald-700/40">
                                        <button
                                            onClick={() => setTokenMode('add')}
                                            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${tokenMode === 'add' ? 'bg-emerald-600 text-white' : 'bg-luxury-obsidian text-emerald-400 hover:bg-emerald-900/30'}`}
                                        >
                                            <PlusIcon className="h-3.5 w-3.5 inline mr-1" />Add
                                        </button>
                                        <button
                                            onClick={() => setTokenMode('subtract')}
                                            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${tokenMode === 'subtract' ? 'bg-performance-pulse text-white' : 'bg-luxury-obsidian text-emerald-400 hover:bg-emerald-900/30'}`}
                                        >
                                            <MinusIcon className="h-3.5 w-3.5 inline mr-1" />Remove
                                        </button>
                                    </div>
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="Qty"
                                        value={tokenInput}
                                        onChange={e => setTokenInput(e.target.value)}
                                        className="w-20 px-3 py-1.5 bg-luxury-obsidian border border-emerald-700/30 rounded-lg text-accent-platinum text-sm focus:outline-none focus:border-emerald-500/60"
                                    />
                                    <button
                                        onClick={handleBulkTokens}
                                        disabled={actionLoading}
                                        className="btn-primary py-1.5 px-4 text-sm disabled:opacity-50 whitespace-nowrap"
                                    >
                                        {actionLoading ? 'Updating…' : 'Apply Tokens'}
                                    </button>
                                </div>

                                {/* Divider */}
                                <div className="hidden sm:block w-px h-6 bg-emerald-700/30" />

                                {/* Remove from venue */}
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={actionLoading}
                                    className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold bg-performance-pulse/20 hover:bg-performance-pulse/30 text-performance-pulse border border-performance-pulse/30 rounded-lg transition-all disabled:opacity-50 whitespace-nowrap"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                    Remove from Venue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── User table ── */}
            {filtered.length === 0 ? (
                <div className="card-luxury p-12 text-center">
                    <UsersIcon className="h-12 w-12 text-emerald-600/40 mx-auto mb-4" />
                    <p className="text-emerald-600/70 font-body">No users match your search criteria.</p>
                </div>
            ) : (
                <div className="card-luxury overflow-hidden">
                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-emerald-600/10">
                            <thead className="bg-luxury-charcoal/50">
                                <tr>
                                    <th className="px-4 py-3 text-left w-10">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                            onChange={toggleAll}
                                            className="w-4 h-4 rounded border-emerald-700/50 text-emerald-600 bg-luxury-obsidian focus:ring-emerald-500 cursor-pointer"
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Email</th>
                                    <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Role</th>
                                    <th className="px-4 py-3 text-center text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Tokens</th>
                                    <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Subscription</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-emerald-600/10">
                                {filtered.map(user => {
                                    const isSelected = selectedIds.has(user.userId);
                                    return (
                                        <tr
                                            key={user.userId}
                                            onClick={() => toggleUser(user.userId)}
                                            className={`cursor-pointer transition-colors duration-150 ${isSelected ? 'bg-emerald-900/20' : 'hover:bg-luxury-charcoal/30'}`}
                                        >
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleUser(user.userId)}
                                                    onClick={e => e.stopPropagation()}
                                                    className="w-4 h-4 rounded border-emerald-700/50 text-emerald-600 bg-luxury-obsidian focus:ring-emerald-500 cursor-pointer"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center flex-shrink-0">
                                                        <span className="text-xs font-display font-bold text-white">
                                                            {user.name?.charAt(0)?.toUpperCase() || '?'}
                                                        </span>
                                                    </div>
                                                    <span className="text-sm font-display font-medium text-accent-platinum">{user.name || '—'}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-emerald-600/80 font-body">{user.email || '—'}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${user.isAdmin ? 'bg-amber-900/30 text-amber-300 border border-amber-700/40' : 'bg-emerald-900/30 text-emerald-300 border border-emerald-700/40'}`}>
                                                    {user.isAdmin ? 'Admin' : 'Member'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center text-sm font-display font-bold text-accent-platinum">{user.tokens ?? 0}</td>
                                            <td className="px-4 py-3">
                                                <MemberSubscriptionCell user={user} onSendLink={setResetMember} onLinkGoCardless={goToGoCardlessLinking} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile card list */}
                    <div className="md:hidden divide-y divide-emerald-600/10">
                        {/* Select all bar */}
                        <div className="px-4 py-3 flex items-center gap-3 bg-luxury-charcoal/30">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                onChange={toggleAll}
                                className="w-4 h-4 rounded border-emerald-700/50 text-emerald-600 bg-luxury-obsidian focus:ring-emerald-500"
                            />
                            <span className="text-xs text-emerald-300/70 font-body">Select all visible</span>
                        </div>
                        {filtered.map(user => {
                            const isSelected = selectedIds.has(user.userId);
                            return (
                                <div
                                    key={user.userId}
                                    onClick={() => toggleUser(user.userId)}
                                    className={`px-4 py-4 flex items-center gap-3 cursor-pointer transition-colors ${isSelected ? 'bg-emerald-900/20' : 'hover:bg-luxury-charcoal/20'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleUser(user.userId)}
                                        onClick={e => e.stopPropagation()}
                                        className="w-4 h-4 rounded border-emerald-700/50 text-emerald-600 bg-luxury-obsidian focus:ring-emerald-500 flex-shrink-0"
                                    />
                                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center flex-shrink-0">
                                        <span className="text-sm font-display font-bold text-white">
                                            {user.name?.charAt(0)?.toUpperCase() || '?'}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-display font-medium text-accent-platinum truncate">{user.name || '—'}</p>
                                        <p className="text-xs text-emerald-600/70 font-body truncate">{user.email || '—'}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold ${user.isAdmin ? 'bg-amber-900/30 text-amber-300' : 'bg-emerald-900/30 text-emerald-300'}`}>
                                                {user.isAdmin ? 'Admin' : 'Member'}
                                            </span>
                                            <span className="text-xs text-emerald-600/60 font-body">{user.tokens ?? 0} tokens</span>
                                        </div>
                                        <div className="mt-2">
                                            <MemberSubscriptionCell user={user} onSendLink={setResetMember} onLinkGoCardless={goToGoCardlessLinking} />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Delete confirmation modal ── */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="card-luxury p-6 max-w-md w-full">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-performance-pulse/20 flex items-center justify-center">
                                <ExclamationTriangleIcon className="h-5 w-5 text-performance-pulse" />
                            </div>
                            <div>
                                <h3 className="text-lg font-display font-bold text-accent-platinum">Remove {selectedCount} user{selectedCount !== 1 ? 's' : ''}?</h3>
                                <p className="text-sm text-emerald-600/70 font-body mt-1">
                                    The following users will be removed from this venue. This action can be reversed by re-adding them.
                                </p>
                            </div>
                        </div>

                        {/* List of affected users */}
                        <ul className="mb-4 max-h-40 overflow-y-auto space-y-1">
                            {users
                                .filter(u => selectedIds.has(u.userId))
                                .map(u => (
                                    <li key={u.userId} className="flex items-center gap-2 text-sm text-emerald-300/80 font-body">
                                        <span className="w-1.5 h-1.5 rounded-full bg-performance-pulse inline-block flex-shrink-0" />
                                        <span className="truncate">{u.name}</span>
                                        <span className="text-emerald-600/50 truncate">{u.email}</span>
                                    </li>
                                ))}
                        </ul>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={actionLoading}
                                className="btn-outline px-4 py-2 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBulkDelete}
                                disabled={actionLoading}
                                className="px-4 py-2 text-sm font-semibold bg-performance-pulse hover:bg-performance-pulse/90 text-white rounded-lg transition-colors disabled:opacity-50"
                            >
                                {actionLoading ? 'Removing…' : `Remove ${selectedCount} user${selectedCount !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Send sign up link (reset sign up) ── */}
            {resetMember && (
                <ResetSignupModal
                    member={resetMember}
                    venueKey={venueKey}
                    plans={plans}
                    onClose={() => setResetMember(null)}
                />
            )}
        </LayoutWrapper>
    );
};

export default BulkUserManagement;

